import {
  getOperationalStartPeriodConfig,
  indexToOperationalMonth,
  operationalMonthToIndex
} from './operational-start-period-config';
import { isCaptureEntry } from './operational-control-aggregator';
import type {
  ActivationSignal,
  DetectionConfidence,
  InstanceOperationalActivationLifecycle,
  OperationalMonth,
  OperationalStartPeriodConfig,
  ResolvedOperationalWindow
} from './types';

const confidenceRank: Record<DetectionConfidence, number> = {
  NONE: 0,
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3
};

const signalConfidence: Record<ActivationSignal, DetectionConfidence> = {
  INSTANCE_CREATED_AT: 'LOW',
  ADMIN_ACTIVATED_AT: 'HIGH',
  ONBOARDING_COMPLETED_AT: 'HIGH',
  FIRST_KPI: 'MEDIUM',
  FIRST_PLAN: 'MEDIUM',
  FIRST_CAPTURE: 'HIGH',
  FIRST_REPORT: 'HIGH'
};

const dateToMonth = (value: string | null | undefined, year: number): OperationalMonth | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() !== year) return null;
  return indexToOperationalMonth(parsed.getMonth());
};

const dateToIso = (year: number, monthIndex: number): string => {
  return new Date(Date.UTC(year, monthIndex, 1, 12, 0, 0)).toISOString();
};

const firstMonthWith = (history: any[], predicate: (entry: any) => boolean): number | null => {
  if (!Array.isArray(history)) return null;
  for (let idx = 0; idx < history.length; idx += 1) {
    if (predicate(history[idx])) return idx;
  }
  return null;
};

const hasCaptureValue = (entry: any): boolean => {
  if (!entry) return false;
  const real = Number(entry.real || 0);
  const hasChecklist = Array.isArray(entry.checklist) && entry.checklist.length > 0;
  const hasElements = Array.isArray(entry.elements) && entry.elements.length > 0;
  const hasActivities = Array.isArray(entry.activities) && entry.activities.length > 0;
  return real > 0 || Boolean(entry.note) || hasChecklist || hasElements || hasActivities;
};

type ActivationDetectionInput = {
  instance: any;
  indicators: any[];
  reports: any[];
  workPlan: any | null;
  planTotal: any | null;
  year: number;
  selectedMonthIndex: number;
  cycleConfig?: OperationalStartPeriodConfig;
};

export const detectInstanceOperationalActivation = ({
  instance,
  indicators,
  reports,
  workPlan,
  planTotal,
  year,
  selectedMonthIndex,
  cycleConfig = getOperationalStartPeriodConfig(year)
}: ActivationDetectionInput): InstanceOperationalActivationLifecycle => {
  const globalStartIndex = operationalMonthToIndex(cycleConfig.globalStartMonth);
  const createdAt = instance?.created_at || instance?.createdAt || null;

  // En el nuevo modelo, buscamos estrictamente el primer mes con meta > 0 de KPIs o Plan
  const firstKpiMonthIndex = indicators.reduce<number | null>((acc, indicator) => {
    const monthIndex = firstMonthWith(indicator.history || [], (entry) => Number(entry?.meta || 0) > 0);
    if (monthIndex === null) return acc;
    return acc === null ? monthIndex : Math.min(acc, monthIndex);
  }, null);

  const hasPlanGoal = Number(planTotal?.total_goal || workPlan?.total_goal || 0) > 0;
  const firstPlanCreatedMonth = dateToMonth(workPlan?.created_at, year);
  const firstPlanMonthIndex = hasPlanGoal
    ? (firstPlanCreatedMonth ? operationalMonthToIndex(firstPlanCreatedMonth) : (firstKpiMonthIndex ?? globalStartIndex))
    : null;

  const candidateSignals: Array<{ source: ActivationSignal; monthIndex: number; date: string }> = [];

  if (firstKpiMonthIndex !== null) {
    candidateSignals.push({
      source: 'FIRST_KPI',
      monthIndex: firstKpiMonthIndex,
      date: dateToIso(year, firstKpiMonthIndex)
    });
  }

  if (firstPlanMonthIndex !== null) {
    candidateSignals.push({
      source: 'FIRST_PLAN',
      monthIndex: firstPlanMonthIndex,
      date: dateToIso(year, firstPlanMonthIndex)
    });
  }

  const allowedSignals = new Set(cycleConfig.activationDetectionPolicy.signals);
  const allowedCandidates = candidateSignals
    .filter((candidate) => allowedSignals.has(candidate.source))
    .sort((a, b) => {
      if (a.monthIndex !== b.monthIndex) return a.monthIndex - b.monthIndex;
      return confidenceRank[signalConfidence[b.source]] - confidenceRank[signalConfidence[a.source]];
    });

  const best = allowedCandidates[0] || null;
  const detectedStartMonth = best ? indexToOperationalMonth(best.monthIndex) : null;
  const detectedStartSource = best?.source || null;
  const detectionConfidence = detectedStartSource ? signalConfidence[detectedStartSource] : 'NONE';

  const firstCaptureMonthIndex = indicators.reduce<number | null>((acc, indicator) => {
    const monthIndex = firstMonthWith(indicator.history || [], hasCaptureValue);
    if (monthIndex === null) return acc;
    return acc === null ? monthIndex : Math.min(acc, monthIndex);
  }, null);
  const firstReport = (reports || [])
    .filter((report) => Number(report.year) === year && Number(report.month) >= 1)
    .sort((a, b) => Number(a.month) - Number(b.month))[0];

  let onboardingState: InstanceOperationalActivationLifecycle['onboardingState'] = 'UNKNOWN';
  if (!best) {
    onboardingState = 'NOT_STARTED';
  } else {
    const isCapturing = indicators.some((indicator) => isCaptureEntry(indicator.history?.[selectedMonthIndex]));
    const hasReport = (reports || []).some((report) => Number(report.year) === year && Number(report.month) - 1 === selectedMonthIndex);
    if (isCapturing || hasReport) {
      onboardingState = 'ACTIVE';
    } else if (selectedMonthIndex <= best.monthIndex + 1) {
      onboardingState = 'IN_PROGRESS';
    } else {
      onboardingState = 'ACTIVE';
    }
  }

  return {
    instanceId: String(instance?.id || ''),
    createdAt,
    adminActivatedAt: null,
    onboardingStartedAt: detectedStartSource === 'INSTANCE_CREATED_AT' ? best?.date || null : null,
    onboardingCompletedAt: null,
    firstKpiAt: firstKpiMonthIndex !== null ? dateToIso(year, firstKpiMonthIndex) : null,
    firstPlanAt: firstPlanMonthIndex !== null ? dateToIso(year, firstPlanMonthIndex) : null,
    firstCaptureAt: firstCaptureMonthIndex !== null ? dateToIso(year, firstCaptureMonthIndex) : null,
    firstReportAt: firstReport ? dateToIso(year, Number(firstReport.month) - 1) : null,
    detectedStartMonth,
    detectedStartSource,
    detectionConfidence,
    onboardingState
  };
};

type ResolutionInput = {
  year: number;
  instanceId: string;
  vpId: string | null;
  selectedMonthIndex: number;
  currentMonthIndex: number;
  lifecycle: InstanceOperationalActivationLifecycle;
  config?: OperationalStartPeriodConfig;
  validMonths?: number[];
};

const canUseLifecycle = (
  lifecycle: InstanceOperationalActivationLifecycle,
  config: OperationalStartPeriodConfig
): boolean => {
  if (!config.activationDetectionPolicy.enabled || !lifecycle.detectedStartMonth) return false;
  if (config.activationMode === 'MANUAL') return false;
  if (lifecycle.detectedStartSource === 'INSTANCE_CREATED_AT') return true;
  return confidenceRank[lifecycle.detectionConfidence] >= confidenceRank[config.activationDetectionPolicy.minimumConfidence];
};

export const resolveOperationalStartPeriod = ({
  year,
  instanceId,
  vpId,
  selectedMonthIndex,
  currentMonthIndex,
  lifecycle,
  config = getOperationalStartPeriodConfig(year),
  validMonths
}: ResolutionInput): ResolvedOperationalWindow => {
  let source: ResolvedOperationalWindow['source'] = 'SYSTEM_FALLBACK';
  let startMonth: OperationalMonth = config.fallbackStartMonth;

  const hasExplicitOverride = !!config.instanceOverrides[instanceId] || (vpId && !!config.vpOverrides[vpId]);
  const hasMetaObligation = lifecycle.detectedStartMonth !== null || hasExplicitOverride;

  if (config.instanceOverrides[instanceId]) {
    source = 'INSTANCE_EXPLICIT_OVERRIDE';
    startMonth = config.instanceOverrides[instanceId];
  } else if (vpId && config.vpOverrides[vpId]) {
    source = 'VP_OVERRIDE';
    startMonth = config.vpOverrides[vpId];
  } else if (canUseLifecycle(lifecycle, config) && lifecycle.detectedStartMonth) {
    source = 'INSTANCE_OPERATIONAL_ACTIVATION';
    startMonth = lifecycle.detectedStartMonth;
  } else if (config.globalStartMonth) {
    source = 'CYCLE_GLOBAL';
    startMonth = config.globalStartMonth;
  }

  const startMonthIndex = operationalMonthToIndex(startMonth);
  const isSelectedMonthBeforeStart = selectedMonthIndex < startMonthIndex;
  const evaluationEndMonth = Math.min(selectedMonthIndex, currentMonthIndex, 11);

  // Instancia activa si tiene obligación y no está antes del inicio
  const isOperationallyActive = hasMetaObligation && !isSelectedMonthBeforeStart;

  const contigMonths = isOperationallyActive
    ? Array.from({ length: evaluationEndMonth - startMonthIndex + 1 }, (_, idx) => startMonthIndex + idx).filter((idx) => idx >= 0)
    : [];

  // expectedMonths SOLO incluye meses elegibles con meta > 0
  const expectedMonths = validMonths
    ? contigMonths.filter((m) => validMonths.includes(m))
    : contigMonths;

  const excludedMonths = Array.from({ length: Math.max(startMonthIndex, 0) }, (_, idx) => idx);

  return {
    year,
    instanceId,
    vpId,
    startMonth,
    startMonthIndex,
    selectedMonthIndex,
    currentMonthIndex,
    isOperationallyActive,
    isSelectedMonthBeforeStart,
    expectedMonths,
    elapsedExpectedMonths: expectedMonths.length,
    excludedMonths,
    activationLifecycle: lifecycle,
    source
  };
};
